// Wraps redux-persist/lib/storage (= window.localStorage) so a throwing
// browser (Safari Private Browsing, ITP eviction) doesn't crash the
// store. Failures fall back to an in-memory map for the current tab,
// and we show a one-time notification telling the user that nothing
// will persist across reloads.

import { notification } from 'antd';
import storage from 'redux-persist/lib/storage';

const inMemoryStore = {};
let warned = false;

const warnOnce = () => {
  if (warned) return;
  warned = true;
  notification.warning({
    message: 'Browser storage is unavailable',
    description:
      'This browser is in Private Browsing mode or has restricted storage. You will need to sign in again each time you reload.'
  });
};

const safeStorage = {
  // We assume storage either works for the entire tab or fails for the
  // entire tab — the realistic failure modes (Private Browsing throw,
  // ITP eviction, disabled by policy) are all-or-nothing. We do NOT
  // handle the contrived mixed-mode case where storage.getItem returns
  // null but a previous setItem fell back to in-memory; in that scenario
  // the read returns null and the caller treats it as no-data. If a real
  // mixed-mode browser ever shows up, switch to a "usingFallback"
  // sticky flag and read from memory once any fallback has been used.
  async getItem(key) {
    try {
      return await storage.getItem(key);
    } catch (e) {
      warnOnce();
      return key in inMemoryStore ? inMemoryStore[key] : null;
    }
  },
  async setItem(key, value) {
    try {
      await storage.setItem(key, value);
    } catch (e) {
      warnOnce();
      inMemoryStore[key] = value;
    }
  },
  async removeItem(key) {
    try {
      await storage.removeItem(key);
    } catch (e) {
      warnOnce();
      delete inMemoryStore[key];
    }
  }
};

export default safeStorage;
